import { useEffect, useRef, useState } from 'react'

import { Page, PageHeader } from '@/components/ui/Page'
import { cn } from '@/lib/cn'

/**
 * The Instagram feed planner (27 Oct 2026): her profile grid as it looks on
 * her phone, without the name, bio and profile picture. Three columns of 3:4
 * tiles (Instagram's profile grid since January 2025); a post itself is 4:5.
 * Planned posts sit above the ones already posted, newest first, exactly as
 * they will appear.
 *
 * For now every post is a placeholder frame. The real posts, captions and
 * tags come from the social_posts tables (handbook docs/05-instagram.md) once
 * the upcoming posts are planned.
 */

interface Post {
  id: string
  number: number
  status: 'planned' | 'posted'
  slides: number
}

const PLACEHOLDERS: Post[] = [
  ...Array.from({ length: 12 }, (_, i) => ({ id: `planned-${i}`, number: 12 - i, status: 'planned' as const, slides: [6, 1, 4, 10, 1, 3][i % 6] ?? 1 })),
  ...Array.from({ length: 9 }, (_, i) => ({ id: `posted-${i}`, number: 9 - i, status: 'posted' as const, slides: [5, 1, 8][i % 3] ?? 1 })),
]

const TAG_ROLES = ['Couple', 'Planner', 'Venue', 'Florist', 'Hair and makeup', 'Dress']

export function FeedPlannerPage() {
  const [open, setOpen] = useState<Post | null>(null)
  const planned = PLACEHOLDERS.filter((post) => post.status === 'planned').length

  return (
    <Page>
      <PageHeader
        eyebrow="Marketing"
        title="Feed planner"
        meta={
          <>
            <span>{planned} planned</span>
            <span>Placeholder frames until we plan the upcoming posts</span>
          </>
        }
      />

      <div className="mx-auto w-full max-w-[430px]">
        {/* The profile tabs: posts, reels, tagged. Only the grid is planned here. */}
        <div className="grid grid-cols-3 border-b">
          <span className="flex justify-center border-b-[1.5px] border-b-ink py-2.5" aria-label="Posts">
            <GridIcon />
          </span>
          <span className="flex justify-center py-2.5 text-taupe" aria-hidden>
            <ReelsIcon />
          </span>
          <span className="flex justify-center py-2.5 text-taupe" aria-hidden>
            <TaggedIcon />
          </span>
        </div>

        <ul className="grid grid-cols-3 gap-[2px] pt-[2px]">
          {PLACEHOLDERS.map((post) => (
            <li key={post.id}>
              <button
                type="button"
                onClick={() => setOpen(post)}
                aria-label={`${post.status === 'planned' ? 'Planned' : 'Posted'} post ${post.number}`}
                className={cn(
                  'relative flex aspect-[3/4] w-full items-center justify-center hover:opacity-85',
                  post.status === 'planned' ? 'bg-paper outline-1 -outline-offset-[6px] outline-dashed outline-[var(--color-rule-strong)]' : 'bg-[var(--color-wash-strong)]',
                )}
              >
                <span className="text-[length:var(--text-2xs)] text-taupe">
                  {post.status === 'planned' ? 'Planned' : 'Posted'} {post.number}
                </span>
                {post.slides > 1 ? (
                  <span className="absolute top-1.5 right-1.5 text-taupe">
                    <CarouselIcon />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {open ? <PostDialog post={open} onClose={() => setOpen(null)} /> : null}
    </Page>
  )
}

/** One post as it opens on the phone: the carousel, then the caption and who to tag. */
function PostDialog({ post, onClose }: { post: Post; onClose: () => void }) {
  const [index, setIndex] = useState(0)
  const touch = useRef<number | null>(null)
  const go = (step: number) => setIndex((current) => Math.min(post.slides - 1, Math.max(0, current + step)))

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') setIndex((current) => Math.min(post.slides - 1, current + 1))
      if (event.key === 'ArrowLeft') setIndex((current) => Math.max(0, current - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, post.slides])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Post ${post.number}`}
      onClick={onClose}
    >
      <div className="w-full max-w-[430px] overflow-hidden rounded-xl bg-[var(--surface-card)] shadow-[var(--shadow-dialog)]" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[length:var(--text-sm)] font-semibold">
            {post.status === 'planned' ? 'Planned' : 'Posted'} post {post.number}
          </span>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[1.5rem] leading-none text-taupe hover:text-ink">
            ×
          </button>
        </div>

        {/* The carousel, 4:5 like a post in the feed */}
        <div
          className="relative aspect-[4/5] overflow-hidden bg-paper"
          onTouchStart={(event) => (touch.current = event.touches[0]?.clientX ?? null)}
          onTouchEnd={(event) => {
            const start = touch.current
            const end = event.changedTouches[0]?.clientX
            if (start !== null && end !== undefined && Math.abs(end - start) > 40) go(end < start ? 1 : -1)
            touch.current = null
          }}
        >
          <div
            className="absolute inset-y-0 left-0 flex transition-transform duration-300"
            style={{ width: `${post.slides * 100}%`, transform: `translateX(-${(index * 100) / post.slides}%)` }}
          >
            {Array.from({ length: post.slides }, (_, slide) => (
              <div key={slide} className="flex h-full shrink-0 items-center justify-center p-3" style={{ width: `${100 / post.slides}%` }}>
                <div className="flex h-full w-full items-center justify-center rounded-sm border border-dashed border-[var(--color-rule-strong)] text-[length:var(--text-sm)] text-taupe">
                  Photo {slide + 1}
                </div>
              </div>
            ))}
          </div>
          {post.slides > 1 ? (
            <>
              <span className="absolute top-3 right-3 rounded-full bg-black/60 px-2 py-0.5 text-[length:var(--text-2xs)] text-white" data-numeric>
                {index + 1}/{post.slides}
              </span>
              {index > 0 ? <Arrow side="left" onClick={() => go(-1)} /> : null}
              {index < post.slides - 1 ? <Arrow side="right" onClick={() => go(1)} /> : null}
            </>
          ) : null}
        </div>
        {post.slides > 1 ? (
          <div className="flex justify-center gap-1 pt-2.5">
            {Array.from({ length: post.slides }, (_, slide) => (
              <span key={slide} className={cn('size-1.5 rounded-full', slide === index ? 'bg-accent' : 'bg-[var(--color-rule-strong)]')} />
            ))}
          </div>
        ) : null}

        <div className="flex flex-col gap-4 px-4 pt-3 pb-5 text-[length:var(--text-sm)]">
          <div>
            <h3 className="label mb-1">Caption</h3>
            <p className="text-taupe">The caption goes here once the post is planned.</p>
          </div>
          <div>
            <h3 className="label mb-1.5">Tag</h3>
            <ul className="flex flex-wrap gap-1.5">
              {TAG_ROLES.map((role) => (
                <li key={role} className="rounded-full bg-paper px-2.5 py-1 text-[length:var(--text-2xs)] text-taupe">
                  {role}: @…
                </li>
              ))}
            </ul>
          </div>
          <dl className="grid grid-cols-[6rem_1fr] gap-y-1 text-[length:var(--text-2xs)]">
            <dt className="text-taupe">Wedding</dt>
            <dd>Not chosen yet</dd>
            <dt className="text-taupe">{post.status === 'planned' ? 'Goes out' : 'Posted'}</dt>
            <dd>Not scheduled yet</dd>
          </dl>
        </div>
      </div>
    </div>
  )
}

function Arrow({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === 'left' ? 'Previous photo' : 'Next photo'}
      className={cn(
        'absolute top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[0.9rem] text-black shadow-[var(--shadow-card)]',
        side === 'left' ? 'left-2' : 'right-2',
      )}
    >
      {side === 'left' ? '‹' : '›'}
    </button>
  )
}

function GridIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
    </svg>
  )
}

function ReelsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M3 8h18M8 3l3 5M14 3l3 5" />
      <path d="M10 11.5v5l4.5-2.5z" fill="currentColor" stroke="none" />
    </svg>
  )
}

function TaggedIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 4h16v13h-5l-3 3-3-3H4z" strokeLinejoin="round" />
      <circle cx="12" cy="9.5" r="2.2" />
      <path d="M8 15c.8-1.6 2.2-2.4 4-2.4s3.2.8 4 2.4" />
    </svg>
  )
}

function CarouselIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 3h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-1V7a2 2 0 0 0-2-2H5V5a2 2 0 0 1 2-2Z" />
      <rect x="3" y="7" width="14" height="14" rx="2" />
    </svg>
  )
}
